import { Box } from "@mui/material";
import React, { useState } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { customerColors } from "features/configure/chartColors";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import AutoCompleteDropdown, {
  SIZE,
} from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import { InputField } from "@metacrm/metacrm-material-ui/dist/InputField";
import { useIntl } from "react-intl";
import _ from "lodash";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import { formatNumberWithComma } from "features/helpers/utils";
// 註解一下
// Bar Chart Component
// props: data - array - required - the data to be displayed in the circle chart.

export default function BehaviorRowBarChart({
  type,
  data,
  chartName,
  filters = [],
  loading = false,
  otherDatas = {},
  showSearchBar,
  searchText,
  setSearchText,
  tooltip,
}) {
  const { formatMessage } = useIntl();
  const { userCount } = otherDatas || {};

  const cleanName = (name) =>
    (name || "").toString().replace(/[^a-zA-Z0-9]/g, "_"); // 替換空格為下劃線

  const richStyles = data.reduce((acc, item) => {
    const key = cleanName(item.name);
    acc[key] = {
      height: 20,
      width: 20,
      align: "center",
      backgroundColor: {
        image: item.icon,
      },
    };
    return acc;
  }, {});
  const gradientColors = customerColors.gradient;

  function formatName(name) {
    let result = name.split("--")[0];
    if (result.length > 10) return result.substring(0, 10) + "...";
    return result;
  }

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: function (params) {
        const { data, name, value } = params[0];

        const fullName = name.split("--")[0];
        const displayName =
          fullName.length > 30
            ? `${fullName.slice(0, 15)}...${fullName.slice(-15)}`
            : fullName;

        return `
              <div style="display: flex; margin: 0px; line-height: 1;">
                <div style="display: flex; position: relative; width: 20px; height: 20px; margin-right: 10px; top: 13px;">
                  <img
                    src="${data.icon}"
                    style="width: 100%; height: 100%; border-radius: 50%;"
                  />
                  <div style="display: flex; position: absolute; bottom: -2px; right: -2px;height: 10px; width: 10px; border: 0.5px solid #fff; border-radius: 50%;">
                    <img
                      src="${data.chain}"
                      style="width: 10px; height: 10px; border-radius: 50%; background-color: #ebeff0;"
                    />
                  </div>
                </div>
                <div style="font-size: 16px; color: #777;">
                  <p style="font-weight: 700; margin: 0; margin-right: 20px">${displayName}</p>
                  ${
                    data.usdValue
                      ? `<p style="margin: 0;">
                        ${formatMessage({
                          id: "global.chart.tag.valueUsd",
                        })}
                      </p>`
                      : ""
                  }
                  <p style="padding-right: 40px; margin: 0;">${formatMessage({
                    id: "global.demographic.%ofUsers",
                  })}</p>
                </div>
                <div style="font-size: 16px; color: #777;text-align: right;">
                  <p style="font-weight: 700; margin: 0;">${formatMessage(
                    {
                      id:
                        type === "topTokenHoldings"
                          ? "global.demographic.holdByUsers"
                          : "global.demographic.interactedByUsers",
                    },
                    { amount: formatNumberWithComma(value) }
                  )}</p>
                  ${
                    data.usdValue
                      ? `<p style="margin: 0;">
                        ${formatNumberWithComma(data.usdValue)}
                      </p>`
                      : ""
                  }
                  <p style="margin: 0;">${formatNumberWithComma(
                    (value * 100) / userCount,
                    2
                  )}%</p>
                </div>
              </div>
            `;
      },
    },
    grid: {
      left: "20px",
      right: "24px",
      top: "10px",
      bottom: "0",
      containLabel: true,
    },
    xAxis: {
      show: true,
      type: "value",
    },
    yAxis: {
      show: true,
      type: "category",
      inverse: true,
      data: data.map((item) => item.name),
      axisLabel: {
        formatter: function (value) {
          return `${formatName(value)} {${cleanName(value)}|}`;
        },
        rich: richStyles,
        color: "#777",
        fontSize: 12,
        fontWeight: 400,
      },
    },
    series: [
      {
        data: data.map((item, index) => ({
          ...item,
          value: item.value,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(
              1,
              0,
              0,
              0, // 渐变方向：从上到下
              [
                { offset: 0, color: gradientColors[index][1] }, // 顶部颜色
                { offset: 1, color: gradientColors[index][0] }, // 底部颜色
              ]
            ),
            borderRadius: [0, 4, 4, 0],
          },
        })),
        barWidth: 16,
        type: "bar",
        label: {
          show: true,
          position: "right",
          fontSize: 10,
          fontWeight: 600,
          color: "rgba(37, 43, 65, 0.64)",
        },
      },
    ],
  };

  return (
    <Box
      sx={{
        width: "100%", // Adjust the width as needed
        height: "100%", // Adjust the height as needed
        backgroundColor: "#FFF",
        borderRadius: "4px",
        minHeight: "300px",
        p: "20px 16px",
      }}
    >
      <Box className="item-flex" justifyContent="space-between" mb="16px">
        <Box fontSize="16px" color="#555">
          {chartName} {tooltip && <IconTooltip content={tooltip} />}
        </Box>
        <Box sx={{ display: "flex", gap: "8px" }}>
          {showSearchBar && (
            <InputField
              size={SIZE.MEDIUM}
              style={{ width: "200px" }}
              startAdornment={
                <i className="meta-crm-icon-ic_search font-size-20" />
              }
              placeholder={formatMessage({
                id: "global.demographic.searchToken",
              })}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          )}
          {filters.map((filter, index) => (
            <Box sx={{ width: "156px" }}>
              <AutoCompleteDropdown
                key={index}
                options={filter.options}
                value={filter.selectedOption}
                placeholder={formatMessage({ id: "menu.placeholder.select" })}
                onChange={(e, v) => filter.setOption(v)}
                size={SIZE.MEDIUM}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {loading ? (
        <LoadingIcon isAbsolute />
      ) : data.length > 0 ? (
        <ReactECharts
          option={option}
          autoResize={true}
          style={{ height: "465px", width: "100%" }}
          notMerge={true}
        />
      ) : (
        <EmptyIconStatus
          sx={{ width: "100%", margin: "81px 0" }}
          wording="later"
        />
      )}
    </Box>
  );
}
